import { ArrowRight, Lock } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';
import { Button } from '@/components/ui/button';
import { api, isApiError } from '../../api';
import { Callout, Section } from '../../components/blocks';
import { DataError } from '../../components/DataError';
import { EntityRef } from '../../components/EntityRef';
import { CorrelationLink, PlainId } from '../../components/Ids';
import { KeyValue } from '../../components/KeyValue';
import { PageHeader, useDocumentTitle } from '../../components/PageHeader';
import { CopyText, Loading } from '../../components/States';
import { formatDateTime } from '../../lib/format';
import { useLabels } from '../../lib/labels';
import { correlationLogQuery, logsLink, traceSearchLink, type TimeRange } from '../../lib/opsQuery';
import { useApi } from '../../lib/useApi';
import { Payload } from '../compliance/vnnic/shared';
import { codeClass, ContactRef, DomainRef, RegistryRef, useVocab, WorkflowRef } from '../domains/refs';
import { COMMAND_STATE, StatusBadge } from '../domains/status';
import { RegistryExchanges } from './RegistryExchanges';

const MINUTE_MS = 60_000;
const DAY_MS = 86_400_000;

/**
 * A log window around the command (`pad` before its start and after its end), never past now nor
 * longer than the Observability API's 30-day range (a command reconciled weeks later).
 */
function aroundCommand(startedAt: string, completedAt: string | null, pad: number): TimeRange {
  const start = new Date(startedAt).getTime() - pad;
  const end = completedAt ? new Date(completedAt).getTime() : new Date(startedAt).getTime() + DAY_MS;
  const last = Math.min(end + pad, Date.now(), start + 30 * DAY_MS - MINUTE_MS);
  return { kind: 'custom', start: new Date(start).toISOString(), end: new Date(Math.max(last, start + MINUTE_MS)).toISOString() };
}

/** `/registry/transactions/:id`: one EPP command with its identifiers, outcome and links (spec §95 chain). */
export function RegistryTransactionPage() {
  const { t } = useTranslation('domains');
  const vocab = useVocab();
  const L = useLabels();
  const { id = '' } = useParams();
  const result = useApi((signal) => api.getRegistryTransaction(id, { signal }), [id]);
  useDocumentTitle(result.status === 'success' ? t('registryTx.docTitle', { operation: L.eppCommand(result.data.operation) }) : t('registryTx.title'));
  const crumbs = [{ label: t('registryTx.crumbSection') }, { label: t('registryTx.crumbList'), to: '/registry/transactions' }];

  if (result.status === 'loading') {
    return (
      <>
        <PageHeader title={t('registryTx.title')} crumbs={crumbs} />
        <Loading rows={4} />
      </>
    );
  }
  if (result.status === 'error') {
    const notFound = isApiError(result.error) && result.error.status === 404;
    return (
      <>
        <PageHeader title={t('registryTx.title')} crumbs={crumbs} />
        <DataError error={result.error} onRetry={notFound ? undefined : result.reload} title={notFound ? t('registryTx.notFound') : t('registryTx.loadFailed')} />
      </>
    );
  }
  const x = result.data;
  const unresolved = x.state === 'UNKNOWN' || x.state === 'RECONCILING' || x.state === 'MANUAL_REVIEW';
  const withheld = !('request_payload' in x) && !('response_payload' in x);
  const objectType = x.object_type.toUpperCase();
  const registryCode = x.registry_code ?? '';

  const object =
    objectType === 'CONTACT' && registryCode ? (
      <ContactRef key="o" registry={registryCode} handle={x.object_key} sub={vocab('object', objectType)} />
    ) : (
      <span key="o" className="inline-flex flex-wrap items-center gap-1">
        {vocab('object', objectType)} <code className={codeClass}>{x.object_key}</code>
      </span>
    );

  return (
    <>
      <PageHeader
        title={<span title={x.operation}>{L.eppCommand(x.operation)}</span>}
        crumbs={[...crumbs, { label: L.eppCommand(x.operation) }]}
        subtitle={t('registryTx.subtitle', { registry: x.registry_name ?? x.registry_code ?? '—' })}
        actions={<StatusBadge map={COMMAND_STATE} value={x.state} />}
      />
      {unresolved ? (
        <Callout tone="serious" title={t('registryTx.unresolvedTitle')} className="mb-6">
          {t('registryTx.unresolved')}
        </Callout>
      ) : null}
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
        <Section title={t('registryTx.command')} className="self-start">
          <KeyValue
            columns={2}
            rows={[
              [t('registryTx.state'), <StatusBadge key="s" map={COMMAND_STATE} value={x.state} />],
              [t('registryTx.attempt'), x.attempt],
              [t('registryTx.object'), object],
              [
                t('registryTx.domain'),
                x.domain_id ? (
                  <DomainRef key="d" ascii={x.domain_name ?? x.domain_id} unicode={x.domain_unicode_name} id={x.domain_id} />
                ) : (
                  '—'
                ),
              ],
              [t('registryTx.registry'), <RegistryRef key="reg" code={x.registry_code} name={x.registry_name} />],
              [t('registryTx.trafficClass'), vocab('trafficClass', x.traffic_class)],
              ['clTRID', <CopyText key="cl" value={x.cltrid} />],
              ['svTRID', x.svtrid ? <CopyText key="sv" value={x.svtrid} /> : t('registryTx.none')],
              [
                t('registryTx.result'),
                x.result_code !== null ? (
                  <span key="r">
                    <code className={codeClass}>{x.result_code}</code> {x.result_message}
                  </span>
                ) : (
                  '—'
                ),
              ],
              [t('registryTx.errorClass'), x.error_class ? vocab('errorClass', x.error_class) : '—'],
              [t('registryTx.started'), formatDateTime(x.started_at)],
              [t('registryTx.sent'), formatDateTime(x.sent_at)],
              [t('registryTx.completed'), formatDateTime(x.completed_at)],
              [t('registryTx.latency'), x.latency_ms !== null ? `${x.latency_ms} ms` : '—'],
              x.retry_of
                ? [t('registryTx.retryOf'), <EntityRef key="retry" label={t('registryTx.retryOfLink')} to={`/registry/transactions/${encodeURIComponent(x.retry_of)}`} id={x.retry_of} />]
                : null,
              [t('registryTx.id'), <CopyText key="id" value={x.id} />],
            ]}
          />
        </Section>

        <Section title={t('registryTx.follow')} description={t('registryTx.followDescription')}>
          <KeyValue
            rows={[
              [t('registryTx.workflow'), <WorkflowRef key="w" id={x.workflow_id} type={x.workflow_type} state={x.workflow_state} />],
              [t('registryTx.correlation'), <CorrelationLink key="c" id={x.correlation_id} n={13} />],
              [t('registryTx.request'), <PlainId key="r" id={x.request_id} n={13} />],
            ]}
          />
          <div className="mt-4 flex flex-wrap gap-2">
            {x.trace_id ? (
              <Button asChild size="sm">
                <Link to={`/ops/traces/${encodeURIComponent(x.trace_id)}`}>
                  {t('registryTx.openTrace')} <ArrowRight />
                </Link>
              </Button>
            ) : null}
            {x.correlation_id ? (
              <>
                <Button asChild variant="outline" size="sm">
                  <Link to={logsLink(correlationLogQuery(x.correlation_id), aroundCommand(x.started_at, x.completed_at, DAY_MS))}>
                    {t('registryTx.logs')} <ArrowRight />
                  </Link>
                </Button>
                <Button asChild variant="outline" size="sm">
                  <Link to={traceSearchLink({ correlation_id: x.correlation_id }, '7d')}>
                    {t('registryTx.traces')} <ArrowRight />
                  </Link>
                </Button>
              </>
            ) : null}
            {/* Every service line of the command (its span carries the clTRID) and its exchanges,
                those under another clTRID too (a read before an update, a session's login). */}
            <Button asChild variant="outline" size="sm">
              <Link to={logsLink(`{service=~".+"} |~ "${x.cltrid}|${x.id}"`, aroundCommand(x.started_at, x.completed_at, 10 * MINUTE_MS))}>
                {t('registryTx.eppLogs')} <ArrowRight />
              </Link>
            </Button>
          </div>
        </Section>

        <Section title={t('registryTx.payloads')} description={withheld ? undefined : t('registryTx.payloadsDescription')} className="xl:col-span-2">
          {withheld ? (
            <p className="flex items-start gap-2 text-sm text-muted-foreground">
              <Lock className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
              <span>{t('registryTx.withheld')}</span>
            </p>
          ) : (
            <div className="grid grid-cols-1 gap-4">
              <div className="grid grid-cols-1 gap-2">
                <h3 className="text-sm font-semibold">{t('registryTx.summary')}</h3>
                <p className="text-xs text-muted-foreground">{t('registryTx.redacted')}</p>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  <Payload label={t('registryTx.requestPayload')} value={x.request_payload} />
                  <Payload label={t('registryTx.responsePayload')} value={x.response_payload} />
                </div>
              </div>
              <div className="grid grid-cols-1 gap-2">
                <h3 className="text-sm font-semibold">{t('registryTx.exchange.title')}</h3>
                <RegistryExchanges cltrid={x.cltrid} startedAt={x.started_at} />
              </div>
            </div>
          )}
        </Section>
      </div>
    </>
  );
}
